import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { Upload, Download, Menu, Play, CheckCircle, XCircle, AlertCircle, FileText, Trash2, Plus } from 'lucide-react'
import * as XLSX from 'xlsx'
import { generateUnifiedJson } from '../../utils/gstr1Helpers';
import { generateGstr7Json } from '../../utils/gstr7Helpers';
import GstSidebar from '../../components/dashboard/GstSidebar'
import ConfirmationDialog from '../../components/common/ConfirmationDialog'

const UploadReturnForm = () => {
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [credentials, setCredentials] = useState(() => {
    const savedCredentials = sessionStorage.getItem('gstCredentials')
    return savedCredentials ? JSON.parse(savedCredentials) : []
  })
  const [uploadStatus, setUploadStatus] = useState({})
  const [isUploading, setIsUploading] = useState(false)

  // Confirmation dialog state - REMOVED

  // Bulk Processing State
  const [bulkQueue, setBulkQueue] = useState([])
  const [currentBulkIndex, setCurrentBulkIndex] = useState(-1)

  // Headless Captcha Modal State
  const [captchaModal, setCaptchaModal] = useState({
    isOpen: false,
    image: '',
    input: '',
    automationId: '',
    context: null
  })

  // Sync disabled to prevent auto-population after clearing data
  // Logic removed: useEffect reading 'gst1Data' and 'gstr7Data' to push to credentials.

  // Save credentials to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('gstCredentials', JSON.stringify(credentials))
  }, [credentials])

  const determineReturnType = (gstin) => {
    if (!gstin || gstin.length < 15) return 'GSTR1' // Default
    // Check 14th character (index 13)
    // "GSTR 7 is when 14th character of the GSTIN is D"
    return gstin.charAt(13).toUpperCase() === 'D' ? 'GSTR7' : 'GSTR1'
  }

  const handleExcelImport = (event) => {
    const file = event.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result)
        const workbook = XLSX.read(data, { type: 'array' })
        const sheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[sheetName]
        const jsonData = XLSX.utils.sheet_to_json(worksheet)

        const importedRows = jsonData.map((row, index) => {
          const gstin = row['GSTIN Master'] || row['GSTIN'] || ''
          return {
            id: Date.now() + index,
            gstinMaster: gstin,
            userId: row['User ID'] || row['UserId'] || '',
            password: row['Password'] || '',
            returnType: determineReturnType(gstin) // Auto-detect return type
          }
        }).filter(row => row.gstinMaster && row.userId && row.password)

        if (importedRows.length === 0) {
          toast.error('No valid data found. Please check Excel headers: GSTIN Master, User ID, Password')
          return
        }

        setCredentials(prev => [...prev, ...importedRows])
        toast.success(`${importedRows.length} records imported successfully`)
      } catch (error) {
        toast.error('Error reading Excel file')
        console.error('Excel import error:', error)
      } finally {
        event.target.value = '' // Reset input
      }
    }
    reader.readAsArrayBuffer(file)
  }

  const handleDownloadTemplate = () => {
    const templateData = [{
      'GSTIN Master': '07AACCE4248H1ZQ',
      'User ID': 'username',
      'Password': 'password'
    }]
    const worksheet = XLSX.utils.json_to_sheet(templateData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Upload_Template')
    XLSX.writeFile(workbook, 'GST_Upload_Template.xlsx')
  }

  const handleDeleteRow = (id) => {
    setCredentials(credentials.filter(c => c.id !== id))
    setUploadStatus(prev => {
      const newStatus = { ...prev }
      // We can't delete by ID easily if status is keyed by GSTIN, 
      // but wait, status was keyed by GSTIN. Let's key by Row ID for uniqueness if duplicates allowed?
      // Previous logic keyed by GSTIN. Let's stick to unique ID if possible, or GSTIN.
      // credentials have 'id'.
      // Let's clean up status for the deleted ID? 
      // Actually status keying by component ID is safer.
      return newStatus
    })
  }




  const loginToGstPortal = async (credential, headless = false) => {
    try {
      // Determine date based on return type keys
      let year, month;
      if (credential.returnType === 'GSTR7') {
        year = localStorage.getItem('gstYear') || new Date().getFullYear().toString();
        month = localStorage.getItem('gstMonth') || (new Date().getMonth() + 1).toString();
      } else {
        year = localStorage.getItem('gst1Year') || new Date().getFullYear().toString();
        month = localStorage.getItem('gst1Month') || (new Date().getMonth() + 1).toString();
      }

      // Find the return data for this GSTIN
      let returnJson = null;
      try {
        if (credential.returnType === 'GSTR7') {
          const allGstr7Data = JSON.parse(localStorage.getItem('gstr7Data') || '[]');
          const gstinData = allGstr7Data.filter(d => d.gstinMaster === credential.gstinMaster || d.gstin === credential.gstinMaster);

          if (gstinData.length > 0) {
            returnJson = generateGstr7Json(credential.gstinMaster, gstinData, month, year);
            console.log('[Upload] Generated GSTR7 JSON:', returnJson);
          }
        } else {
          // GSTR1: Use unified JSON generator
          const gstr1Data = JSON.parse(localStorage.getItem('gstr1Data') || '[]');
          const gstinData = gstr1Data.filter(d => d.gstinMaster === credential.gstinMaster);

          if (gstinData.length > 0) {
            // Read selected sections from localStorage
            const savedSections = localStorage.getItem('gstr1SelectedSections')
            const sections = savedSections ? JSON.parse(savedSections) : null

            returnJson = generateUnifiedJson(credential.gstinMaster, gstinData, sections, month, year);
            console.log('[Upload] Generated GSTR1 JSON with sections:', sections, returnJson);
          } else {
            console.warn('[Upload] No GSTR1 data found for GSTIN:', credential.gstinMaster);
          }
        }
      } catch (e) {
        console.warn('Failed to retrieve return JSON from local storage', e);
      }

      console.log('Sending returnJson found:', !!returnJson);

      const response = await fetch('/api/login-gst-portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          credentials: {
            userId: credential.userId,
            password: credential.password
          },
          gstin: credential.gstinMaster, // Send GSTIN for file path creation
          returnJson, // Send the actual data
          returnType: credential.returnType, // Send return type (GSTR1 or GSTR7)
          year,
          month,
          headless // Pass headless flag
        })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error || 'Login failed')

      if (headless) return result; // Return full result for headless handling
      return true
    } catch (error) {
      throw error
    }
  }

  const handleUploadClick = async (row) => {
    if (isUploading) return
    if (!row.userId || !row.password) {
      toast.error('Please fill User ID and Password');
      return;
    }

    setIsUploading(true)
    const rowId = row.id
    setUploadStatus(prev => ({
      ...prev,
      [rowId]: { status: 'loading', message: 'Initiating login...' }
    }))

    try {
      setUploadStatus(prev => ({
        ...prev,
        [rowId]: { status: 'loading', message: 'Opening Browser...' }
      }))

      await loginToGstPortal(row, false) // Explicitly false

      setUploadStatus(prev => ({
        ...prev,
        [rowId]: { status: 'success', message: 'Browser opened. Monitoring upload...' }
      }))
      toast.success(`Browser launched for ${row.gstinMaster}. Please fill Captcha.`)

      // Start Polling for Status
      startPolling(row.gstinMaster, rowId)

    } catch (error) {
      setUploadStatus(prev => ({
        ...prev,
        [rowId]: { status: 'error', message: error.message }
      }))
      toast.error(`Login failed: ${error.message}`)
      setIsUploading(false)
    }
  }

  const startHeadlessAuth = async (row) => {
    const rowId = row.id
    setUploadStatus(prev => ({
      ...prev,
      [rowId]: { status: 'loading', message: 'Starting background connection...' }
    }))

    try {
      const result = await loginToGstPortal(row, true) // Headless = true

      if (result.captchaImage) {
        setCaptchaModal({
          isOpen: true,
          image: result.captchaImage,
          input: '',
          automationId: result.automationId,
          context: result.context,
          rowId: rowId
        })
        setUploadStatus(prev => ({
          ...prev,
          [rowId]: { status: 'loading', message: 'Waiting for Captcha...', canCancel: true, automationId: result.automationId }
        }))
      } else {
        throw new Error('No captcha received')
      }

    } catch (error) {
      setUploadStatus(prev => ({
        ...prev,
        [rowId]: { status: 'error', message: error.message }
      }))
      toast.error(`Headless Start failed for ${row.gstinMaster}: ${error.message}`)

      // If in bulk mode, move to next after a delay
      // Access queue via state is risky due to closure, but currentBulkIndex is what we relay on.
      // WE MUST CHECK if we are in bulk mode.
      // Since we can't easily access the FUTURE state of currentBulkIndex here without refs, 
      // we check if we have a queue.
      if (bulkQueue.length > 0) {
        console.log('Bulk mode: Error in start, moving to next...');
        // Pass the index we *intended* to process?
        // Actually simpler: re-read the queue or just pass callback?
        // Best way: call processNextBulkItem with updated index based on current
        // BUT currentBulkIndex might be stale? 
        // NO, startHeadlessAuth is called BY processNextBulkItem usually.
        setTimeout(() => {
          // We need to find the NEXT item.
          // Since we don't have the index passed in, we have to look it up or rely on caller.
          // Let's rely on finding rowId in bulkQueue.
          const idx = bulkQueue.indexOf(rowId);
          if (idx !== -1 && idx < bulkQueue.length - 1) {
            processNextBulkItem(idx + 1);
          } else {
            // End of queue
            setBulkQueue([]);
            setCurrentBulkIndex(-1);
            setIsUploading(false);
            toast.success("Batch Completed (with errors)");
          }
        }, 2000);
      } else {
        setIsUploading(false); // Only release if not bulk
      }
    }
  }

  const handleHeadlessClick = (row) => {
    if (isUploading) return
    if (!row.userId || !row.password) {
      toast.error('Please fill User ID and Password');
      return;
    }
    setIsUploading(true)
    startHeadlessAuth(row)
  }

  const handleProcessAll = () => {
    if (isUploading || credentials.length === 0) return

    // Validation
    const invalidRows = credentials.filter(c => !c.userId || !c.password);
    if (invalidRows.length > 0) {
      toast.error(`Please fill User ID and Password for: ${invalidRows.map(r => r.gstinMaster).join(', ')}`);
      return;
    }

    // Filter rows that are eligible? For now, do all.
    const queue = credentials.map(c => c.id)
    setBulkQueue(queue)
    setCurrentBulkIndex(0)
    setIsUploading(true)

    toast.info(`Starting Bulk Process for ${queue.length} returns...`)

    // Start first
    const firstRow = credentials.find(c => c.id === queue[0])
    if (firstRow) startHeadlessAuth(firstRow)
  }

  const handleStopBulk = () => {
    setBulkQueue([]);
    setCurrentBulkIndex(-1);
    // Find current running row
    const currentRow = credentials.find(c => uploadStatus[c.id]?.status === 'loading');
    if (currentRow) {
      const authId = uploadStatus[currentRow.id]?.automationId;
      if (authId) {
        console.log('Stopping bulk: cancelling current', authId);
        // We call api manually to avoid UI flicker or just force cancel
        fetch(`/api/automation/${authId}`, { method: 'DELETE' }).catch(e => console.error(e));
        setUploadStatus(prev => ({
          ...prev,
          [currentRow.id]: { status: 'error', message: 'Batch Queue Stopped.' }
        }));
      }
    }
    setIsUploading(false);
    toast.info('Batch processing stopped.');
  }

  const processNextBulkItem = (nextIndex) => {
    if (nextIndex >= bulkQueue.length) {
      // Finished
      setBulkQueue([])
      setCurrentBulkIndex(-1)
      setIsUploading(false)
      toast.success("All returns processed!")
      return
    }

    setCurrentBulkIndex(nextIndex)
    const nextId = bulkQueue[nextIndex]
    const nextRow = credentials.find(c => c.id === nextId)

    if (nextRow) {
      toast.info(`Auto-processing next: ${nextRow.gstinMaster}`)
      startHeadlessAuth(nextRow)
    } else {
      // Row missing? skip
      processNextBulkItem(nextIndex + 1)
    }
  }

  const handleCaptchaSubmit = async () => {
    if (!captchaModal.input) {
      toast.error('Please enter the captcha')
      return;
    }

    const { automationId, input, context, rowId } = captchaModal;

    // Show processing in modal
    // Actually we can just close modal and show processing in row
    setCaptchaModal(prev => ({ ...prev, isOpen: false }));
    setUploadStatus(prev => ({
      ...prev,
      [rowId]: { status: 'loading', message: 'Submitting Captcha...', canCancel: true, automationId }
    }))

    try {
      const response = await fetch('/api/submit-captcha', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          automationId,
          captchaText: input,
          context
        })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      toast.success('Captcha submitted! Running in background...');
      setUploadStatus(prev => ({
        ...prev,
        [rowId]: { status: 'loading', message: 'Processing in Background...', canCancel: true, automationId }
      }));

      // Start polling
      startPolling(context.gstin, rowId, true, () => {
        // On Complete callback
        // Check if we are in bulk mode.
        // Since this callback is created at Submit time, we can bake in the logic.
        // BUT currentBulkIndex state inside this callback usage might be stale if we relied on state directly without deps.
        // However, we can use the bulkQueue from state refs if needed OR simpler:
        // Just look up the next item based on current ID or just increment the index we knew about.
        // We know 'rowId'.
        // Let's Find index in bulkQueue.

        if (bulkQueue.length > 0) {
          const idx = bulkQueue.indexOf(rowId);
          if (idx !== -1) {
            processNextBulkItem(idx + 1);
          } else {
            // Fallback if ID invalid? Close
            setIsUploading(false);
          }
        } else {
          // Single Mode Finished
          setIsUploading(false);
        }
      });

      // Do NOT set isUploading(false) here, because we want to keep the screen frozen 
      // until the polling finishes (which calls the callback above).

    } catch (e) {
      toast.error(`Captcha Error: ${e.message}`);
      setUploadStatus(prev => ({
        ...prev,
        [rowId]: { status: 'error', message: `Captcha Error: ${e.message}` }
      }));
      // If error here, break the chain? Or continue?
      // Usually user can retry captcha.
      // If we close, we break.
      setIsUploading(false);
    }
  }

  const handleCancelHeadless = async (automationId, rowId) => {
    try {
      await fetch(`/api/automation/${automationId}`, { method: 'DELETE' });
      setUploadStatus(prev => ({
        ...prev,
        [rowId]: { status: 'error', message: 'Cancelled by user.' }
      }));
      toast.info('Background process cancelled.');
    } catch (e) {
      console.error('Cancel failed', e);
    }
  }

  const startPolling = (gstin, rowId, autoStop = false, onComplete = null) => {
    const pollInterval = setInterval(async () => {
      try {
        const res = await fetch(`/api/gst-upload-status/${gstin}`);
        const json = await res.json();

        if (json.success && json.status && json.status.status) {
          const statusText = json.status.status;
          const details = json.status.details;

          let uiStatus = 'success';
          if (statusText.toLowerCase().includes('error')) uiStatus = 'error';
          if (statusText.toLowerCase().includes('processing')) uiStatus = 'loading';

          let msg = `Status: ${statusText}`;
          if (details && details.errorReport) {
            msg += `. ${details.errorReport}`;
          }

          // keep canCancel if still loading
          const canCancel = uiStatus === 'loading';
          const currentAuthId = uploadStatus[rowId]?.automationId;

          setUploadStatus(prev => ({
            ...prev,
            [rowId]: { status: uiStatus, message: msg, text: statusText, canCancel, automationId: currentAuthId }
          }));

          // Stop polling if final state
          if (statusText.includes('Processed') || statusText.includes('Error')) {
            clearInterval(pollInterval);
            if (uiStatus === 'error') {
              toast.error(`Upload Failed: ${statusText}`);
            } else {
              toast.success('Upload Processed Successfully!');
            }

            if (onComplete) {
              onComplete();
            } else {
              if (!autoStop && isUploading) setIsUploading(false);
            }
          }
        }
      } catch (e) {
        console.error('Polling error', e);
      }
    }, 2000);

    // Stop polling after 5 minutes just in case
    setTimeout(() => {
      clearInterval(pollInterval);
      // If timed out, treat as done?
      if (onComplete) onComplete();
    }, 300000);
  }

  const handleCellChange = (id, field, value) => {
    setCredentials(prev => prev.map(row => {
      if (row.id === id) {
        const updatedRow = { ...row, [field]: value }
        if (field === 'gstinMaster') {
          updatedRow.returnType = determineReturnType(value)
        }
        return updatedRow
      }
      return row
    }))
  }

  const handleAddRow = () => {
    setCredentials(prev => [...prev, {
      id: Date.now(),
      gstinMaster: '',
      userId: '',
      password: '',
      returnType: 'GSTR1'
    }])
  }

  const handleClearStatus = () => {
    setUploadStatus({})
    toast.info('All statuses cleared')
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <GstSidebar
        onSidebarStateChange={(state) => setSidebarOpen(state.isOpen)}
        onNavigationAttempt={(path) => navigate(path)}
      />

      <div className="flex-1 flex flex-col ml-64 transition-all duration-300">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200 px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Upload Return</h1>
              <p className="text-sm text-gray-500">Bulk upload GSTR1 and GSTR7 data</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleAddRow}
                className="flex items-center px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors border border-gray-200 shadow-sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Row
              </button>
              {currentBulkIndex === -1 && (
                <button
                  onClick={handleProcessAll}
                  className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors shadow-sm"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Process All Headless
                </button>
              )}
              {/* Show Stop Button if processing batch */}
              {currentBulkIndex !== -1 && (
                <button
                  onClick={handleStopBulk}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-sm ml-2"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Stop Batch ({bulkQueue.length - currentBulkIndex} left)
                </button>

              )}
              <button
                onClick={handleDownloadTemplate}
                className="flex items-center px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </button>
              <button
                onClick={handleClearStatus}
                className="flex items-center px-4 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Status
              </button>
              <div className="relative">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleExcelImport}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <button className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm">
                  <Upload className="w-4 h-4 mr-2" />
                  Import Excel
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="bg-white rounded-lg border border-gray-300 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className="w-10 bg-gray-100 border-r border-b border-gray-300 px-2 py-2 text-center text-gray-500 font-medium">#</th>
                    <th className="bg-gray-100 border-r border-b border-gray-300 px-4 py-2 text-left font-semibold text-gray-700 w-64">GSTIN Master</th>
                    <th className="bg-gray-100 border-r border-b border-gray-300 px-4 py-2 text-left font-semibold text-gray-700 w-48">User ID</th>
                    <th className="bg-gray-100 border-r border-b border-gray-300 px-4 py-2 text-left font-semibold text-gray-700 w-48">Password</th>
                    <th className="bg-gray-100 border-r border-b border-gray-300 px-4 py-2 text-left font-semibold text-gray-700 w-32">Return Type</th>
                    <th className="bg-gray-100 border-r border-b border-gray-300 px-4 py-2 text-center font-semibold text-gray-700 w-40">Status</th>
                    <th className="bg-gray-100 border-r border-b border-gray-300 px-4 py-2 text-center font-semibold text-gray-700 w-24">Action</th>
                    <th className="bg-gray-100 border-r border-b border-gray-300 px-4 py-2 text-center font-semibold text-gray-700 w-24">Headless</th>
                    <th className="bg-gray-100 border-b border-gray-300 px-2 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {credentials.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="p-12 text-center text-gray-400">
                        No data. Add a row manually or import from Excel.
                      </td>
                    </tr>
                  ) : (
                    credentials.map((row, index) => {
                      const status = uploadStatus[row.id]
                      return (
                        <tr key={row.id} className="group hover:bg-gray-50">
                          <td className="border-r border-b border-gray-300 bg-gray-50 text-center text-gray-500 text-xs">
                            {index + 1}
                          </td>
                          <td className="border-r border-b border-gray-300 p-0">
                            <input
                              type="text"
                              value={row.gstinMaster}
                              onChange={(e) => handleCellChange(row.id, 'gstinMaster', e.target.value.toUpperCase())}
                              className="w-full h-full px-3 py-2 bg-transparent border-none focus:ring-2 focus:ring-inset focus:ring-blue-500 outline-none font-medium text-gray-900 placeholder-gray-400"
                              placeholder="Enter GSTIN"
                              maxLength={15}
                            />
                          </td>
                          <td className="border-r border-b border-gray-300 p-0">
                            <input
                              type="text"
                              value={row.userId}
                              onChange={(e) => handleCellChange(row.id, 'userId', e.target.value)}
                              className="w-full h-full px-3 py-2 bg-transparent border-none focus:ring-2 focus:ring-inset focus:ring-blue-500 outline-none text-gray-600 placeholder-gray-400"
                              placeholder="Username"
                            />
                          </td>
                          <td className="border-r border-b border-gray-300 p-0">
                            <input
                              type="password"
                              value={row.password}
                              onChange={(e) => handleCellChange(row.id, 'password', e.target.value)}
                              className="w-full h-full px-3 py-2 bg-transparent border-none focus:ring-2 focus:ring-inset focus:ring-blue-500 outline-none text-gray-600 placeholder-gray-400"
                              placeholder="••••••••"
                            />
                          </td>
                          <td className="border-r border-b border-gray-300 px-3 py-2 bg-gray-50">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
                                                    ${row.returnType === 'GSTR7' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                              {row.returnType}
                            </span>
                          </td>
                          <td className="border-r border-b border-gray-300 px-2 py-1 text-center bg-white">
                            {status ? (
                              <div className="flex items-center space-x-2 cursor-help px-2" title={status.message || ''}>
                                <div className="flex-shrink-0">
                                  {status.status === 'loading' && <AlertCircle className="w-4 h-4 text-yellow-500 animate-spin" />}
                                  {status.status === 'success' && <CheckCircle className="w-4 h-4 text-green-500" />}
                                  {status.status === 'error' && <XCircle className="w-4 h-4 text-red-500" />}
                                </div>
                                <span className={`text-xs font-medium truncate ${status.status === 'error' ? 'text-red-600' :
                                  status.status === 'success' ? 'text-green-600' : 'text-gray-500'
                                  }`}>
                                  {status.text || status.status || 'Loading...'}
                                </span>
                              </div>
                            ) : <span className="text-gray-300">-</span>}
                          </td>
                          <td className="border-r border-b border-gray-300 p-1 text-center bg-white">
                            <button
                              onClick={() => handleUploadClick(row)}
                              disabled={isUploading || status?.status === 'loading'}
                              title="Browser Mode"
                              className={`inline-flex items-center justify-center p-1.5 rounded hover:bg-gray-100 transition-colors
                                                        ${status?.status === 'success' ? 'opacity-50 cursor-not-allowed' : 'text-blue-600'}
                                                    `}
                            >
                              <Play className="w-4 h-4 fill-current" />
                            </button>
                          </td>
                          <td className="border-r border-b border-gray-300 p-1 text-center bg-white">
                            {status && status.canCancel ? (
                              <button
                                onClick={() => handleCancelHeadless(status.automationId, row.id)}
                                className="text-red-500 hover:text-red-700 text-xs px-2 py-1 border border-red-200 rounded hover:bg-red-50"
                              >
                                Cancel
                              </button>
                            ) : (
                              <button
                                onClick={() => handleHeadlessClick(row)}
                                disabled={isUploading || status?.status === 'loading'}
                                title="Background Mode"
                                className={`inline-flex items-center justify-center p-1.5 rounded hover:bg-gray-100 transition-colors
                                                            ${status?.status === 'success' ? 'opacity-50 cursor-not-allowed' : 'text-purple-600'}
                                                        `}
                              >
                                <div className="flex flex-col items-center">
                                  <Play className="w-4 h-4 fill-current" />
                                  <span className="text-[9px] leading-tight">Headless</span>
                                </div>
                              </button>
                            )}
                          </td>
                          <td className="border-b border-gray-300 text-center p-1">
                            <button
                              onClick={() => handleDeleteRow(row.id)}
                              className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Helper Info */}
          <div className="mt-6 flex items-start p-4 bg-blue-50 text-blue-800 rounded-lg border border-blue-100">
            <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold mb-1">How it works:</p>
              <ul className="list-disc list-inside space-y-1 opacity-90">
                <li><strong>Import Excel:</strong> Use the template to bulk upload credentials.</li>
                <li><strong>Add Row:</strong> Manually type credentials into the grid cells.</li>
                <li><strong>Auto-Detect:</strong> Return Type (GSTR1/GSTR7) matches the 15th char of GSTIN ('D' = GSTR7).</li>
                <li><strong>Upload:</strong> Click the Play icon (Browser mode) to launch browser. After you manually log in, the system will auto-navigate to the Return Dashboard.</li>
                <li><strong>Headless:</strong> Click the Purple Play icon to run in background. You will be prompted to enter Captcha, and the system will handle the rest without opening a browser window.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={3000} />

      {/* Captcha Modal */}
      {captchaModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 w-96 shadow-2xl transform transition-all">
            <h3 className="text-lg font-bold mb-4 text-gray-800">Enter CAPTCHA</h3>
            <div className="mb-4 flex justify-center border-2 border-dashed border-gray-200 p-2 bg-gray-50 rounded-lg min-h-[5rem]">
              {captchaModal.image ? (
                <img src={captchaModal.image} alt="Captcha" className="h-16 object-contain" />
              ) : (
                <div className="flex flex-col items-center justify-center text-gray-400">
                  <span className="text-xs">Loading image...</span>
                </div>
              )}
            </div>

            <input
              type="text"
              value={captchaModal.input}
              onChange={(e) => setCaptchaModal(prev => ({ ...prev, input: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && handleCaptchaSubmit()}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none mb-6 text-lg tracking-widest text-center uppercase"
              placeholder="Enter text shown above"
              autoFocus
            />

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setCaptchaModal({ isOpen: false, image: '', input: '', automationId: '', context: null });
                  setIsUploading(false);
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 font-medium rounded-lg transition-colors"
                tabIndex={0}
              >
                Cancel
              </button>
              <button
                onClick={handleCaptchaSubmit}
                className="px-6 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 shadow-sm transition-colors focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                tabIndex={0}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Processing Blocking Overlay */}


    </div>
  )
}

export default UploadReturnForm